import { t, localeTag } from './i18n.js';
import { useEffect, useMemo, useState, useRef } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Bell } from 'lucide-react';
import { api, send } from './api.js';
import { GuestPicker, guestAddresses } from './GuestPicker.js';
import { Notice, Modal } from './components.js';
import { zones } from './time.js';
const isoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dateLabel = (d: Date) =>
  d.toLocaleDateString(localeTag(), { weekday: 'short', day: 'numeric', month: 'short' });
export interface CalendarSeed {
  date: string;
  create?: boolean;
  key: number;
}
export function Calendar({ seed }: { seed?: CalendarSeed } = {}) {
  const [anchor, setAnchor] = useState(isoDate(new Date())),
    [view, setView] = useState(window.innerWidth < 600 ? 'day' : 'month');
  const [zone, setZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Abidjan',
  );
  const [zoneInput, setZoneInput] = useState(zone);
  const [events, setEvents] = useState<any[]>([]),
    [selected, setSelected] = useState<any>(),
    [form, setForm] = useState<any>();
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [reminders, setReminders] = useState<any[]>([]);
  const timeScroll = useRef<HTMLDivElement>(null);
  const loadVersion = useRef(0);
  useEffect(() => {
    if (timeScroll.current) timeScroll.current.scrollTop = view === 'month' ? 0 : 7 * 60;
  }, [view]);
  const [requestKey, setRequestKey] = useState('');
  const [showOwned, setShowOwned] = useState(true),
    [showInvited, setShowInvited] = useState(true);
  const miniDays = useMemo(() => {
    const d = new Date(anchor + 'T12:00:00');
    d.setDate(1);
    d.setDate(1 - ((d.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => {
      const n = new Date(d);
      n.setDate(d.getDate() + i);
      return n;
    });
  }, [anchor]);
  const days = useMemo(() => {
    const d = new Date(anchor + 'T12:00:00');
    if (view === 'month') d.setDate(1);
    if (view !== 'day') d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return Array.from({ length: view === 'month' ? 42 : view === 'week' ? 7 : 1 }, (_, i) => {
      const n = new Date(d);
      n.setDate(d.getDate() + i);
      return n;
    });
  }, [anchor, view]);
  const load = async () => {
    const version = ++loadVersion.current;
    const from = new Date(days[0]);
    from.setDate(from.getDate() - 1);
    from.setHours(0, 0, 0, 0);
    const to = new Date(days.at(-1)!);
    to.setDate(to.getDate() + 2);
    to.setHours(0, 0, 0, 0);
    const [nextEvents, nextReminders] = await Promise.all([
      api<any[]>(
        `/calendar/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
      ),
      api<any[]>('/calendar/reminders'),
    ]);
    if (version === loadVersion.current) {
      setEvents(nextEvents);
      setReminders(nextReminders);
    }
  };
  useEffect(() => {
    let disposed = false;
    const refresh = () => {
      if (!disposed) void load().catch((e) => !disposed && setError(e.message));
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => {
      disposed = true;
      loadVersion.current++;
      clearInterval(timer);
    };
  }, [anchor, view]);
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const time = (s: string) =>
    new Intl.DateTimeFormat(localeTag(), {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(s));
  const dateKey = (s: string) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(s));
  const occurrences = events
    .filter((e) => (e.editable ? showOwned : showInvited))
    .flatMap((e) => e.occurrences.map((o: any) => ({ ...e, occurrence: o })));
  const create = (date = anchor, hour = 9) => {
    const end = new Date(date + 'T12:00:00');
    if (hour === 23) end.setDate(end.getDate() + 1);
    setSelected(undefined);
    setRequestKey(crypto.randomUUID());
    setForm({
      title: '',
      description: '',
      location: '',
      start: date + 'T' + String(hour).padStart(2, '0') + ':00',
      end: isoDate(end) + 'T' + String((hour + 1) % 24).padStart(2, '0') + ':00',
      timezone: zone,
      frequency: 'once',
      count: 1,
      reminderMinutes: 15,
      attendees: '',
      guestQuery: '',
    });
  };
  const navigate = (direction: number) => {
    const d = new Date(anchor + 'T12:00:00');
    if (view === 'month') {
      d.setDate(1);
      d.setMonth(d.getMonth() + direction);
    } else d.setDate(d.getDate() + direction * (view === 'week' ? 7 : 1));
    setAnchor(isoDate(d));
  };
  // Opened from the agenda side panel: show the requested day, optionally with a new event form.
  useEffect(() => {
    if (!seed) return;
    setAnchor(seed.date);
    setView('day');
    if (seed.create) create(seed.date, 9);
  }, [seed?.key]);
  return (
    <main className="calendar-workspace">
      <div className="calendar-heading">
        <div>
          <h1>
            <CalendarDays size={25} /> {t('Mon agenda')}
          </h1>
          <p>{t('Vos rendez-vous et invitations, dans votre fuseau horaire.')}</p>
        </div>
        <button className="primary" onClick={() => create()}>
          <Plus size={18} /> {t('Nouvel événement')}
        </button>
      </div>
      {error && <Notice type="error">{error}</Notice>}
      {reminders.map((r) => (
        <div role="status" className="calendar-reminder" key={r.occurrence}>
          <Bell size={18} />
          <strong>{r.title}</strong> · {time(r.start)}
          <button
            onClick={() =>
              void act(async () => {
                await send('/calendar/reminders/dismiss', { occurrence: r.occurrence });
                await load();
              })
            }
          >
            {t('Fermer le rappel')}
          </button>
        </div>
      ))}
      <div className="calendar-toolbar">
        <button aria-label={t('Période précédente')} onClick={() => navigate(-1)}>
          <ChevronLeft size={18} />
        </button>
        <button onClick={() => setAnchor(isoDate(new Date()))}>{t('Aujourd’hui')}</button>
        <button aria-label={t('Période suivante')} onClick={() => navigate(1)}>
          <ChevronRight size={18} />
        </button>
        <strong>
          {new Date(anchor + 'T12:00:00').toLocaleDateString(localeTag(), {
            month: 'long',
            year: 'numeric',
          })}
        </strong>
        <label>
          Vue{' '}
          <select aria-label={t('Vue')} value={view} onChange={(e) => setView(e.target.value)}>
            <option value="day">{t('Jour')}</option>
            <option value="week">{t('Semaine')}</option>
            <option value="month">{t('Mois')}</option>
          </select>
        </label>
        <label>
          Fuseau{' '}
          <input
            list="calendar-zones"
            value={zoneInput}
            onChange={(e) => setZoneInput(e.target.value)}
            onBlur={(e) => {
              try {
                new Intl.DateTimeFormat(localeTag(), { timeZone: e.target.value });
                setZone(e.target.value);
              } catch {
                setZoneInput(zone);
                setError(t('Fuseau inconnu. Choisissez un fuseau de la liste.'));
              }
            }}
          />
        </label>
        <datalist id="calendar-zones">
          {[...new Set([...zones, zone])].map((z) => (
            <option key={z}>{z}</option>
          ))}
        </datalist>
      </div>
      <div className="calendar-layout">
        <aside className="calendar-sidebar" aria-label={t('Navigation de l’agenda')}>
          <h2>
            {new Date(anchor + 'T12:00:00').toLocaleDateString(localeTag(), {
              month: 'long',
              year: 'numeric',
            })}
          </h2>
          <div className="mini-calendar">
            {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((label, i) => (
              <span key={'weekday' + i}>{label}</span>
            ))}
            {miniDays.map((d) => (
              <button
                key={isoDate(d)}
                aria-label={d.toLocaleDateString(localeTag(), { dateStyle: 'full' })}
                aria-pressed={isoDate(d) === anchor}
                className={isoDate(d) === anchor ? 'selected' : ''}
                onClick={() => setAnchor(isoDate(d))}
              >
                {d.getDate()}
              </button>
            ))}
          </div>
          <h2>{t('Mes agendas')}</h2>
          <label>
            <input
              type="checkbox"
              checked={showOwned}
              onChange={(e) => setShowOwned(e.target.checked)}
            />{' '}
            {t('Mes événements')}
          </label>
          <label>
            <input
              type="checkbox"
              checked={showInvited}
              onChange={(e) => setShowInvited(e.target.checked)}
            />{' '}
            {t('Invitations reçues')}
          </label>
          <p className="muted">{zone}</p>
        </aside>
        <div className="calendar-surface">
          <div
            className={`calendar-weekdays ${view !== 'month' ? 'timed' : ''}`}
            style={{ gridTemplateColumns: `repeat(${days.length === 1 ? 1 : 7}, minmax(0,1fr))` }}
          >
            {(view === 'month' ? days.slice(0, 7) : days).map((d) => (
              <div key={isoDate(d)}>
                {d.toLocaleDateString(localeTag(), { weekday: 'short' })}
                {view !== 'month' && <strong>{d.getDate()}</strong>}
              </div>
            ))}
          </div>
          <div
            ref={timeScroll}
            className={view === 'month' ? 'calendar-grid calendar-month' : 'calendar-time-scroll'}
          >
            <div
              className={view === 'month' ? 'calendar-month-cells' : 'calendar-time-grid'}
              style={
                view === 'month'
                  ? undefined
                  : { gridTemplateColumns: `48px repeat(${days.length}, minmax(0,1fr))` }
              }
            >
              {view !== 'month' && (
                <div className="calendar-hours">
                  {Array.from({ length: 24 }, (_, h) => (
                    <span key={h} style={{ top: h * 60 }}>
                      {String(h).padStart(2, '0')}:00
                    </span>
                  ))}
                </div>
              )}
              {days.map((d) => {
                const day = isoDate(d);
                const entries = occurrences
                  .filter(
                    (e) =>
                      dateKey(e.occurrence.start) <= day &&
                      dateKey(new Date(Date.parse(e.occurrence.end) - 1).toISOString()) >= day,
                  )
                  .sort((a, b) => a.occurrence.start.localeCompare(b.occurrence.start));
                const minute = (instant: string) => {
                  const parts = new Intl.DateTimeFormat('en-GB', {
                    timeZone: zone,
                    hour: '2-digit',
                    minute: '2-digit',
                    hourCycle: 'h23',
                  }).formatToParts(new Date(instant));
                  return (
                    Number(parts.find((p) => p.type === 'hour')!.value) * 60 +
                    Number(parts.find((p) => p.type === 'minute')!.value)
                  );
                };
                const segments = entries.map((e) => ({
                  e,
                  start: dateKey(e.occurrence.start) < day ? 0 : minute(e.occurrence.start),
                  end: dateKey(e.occurrence.end) > day ? 1440 : minute(e.occurrence.end),
                }));
                // Each connected overlap group shares columns; simultaneous meetings remain selectable.
                let group: typeof segments = [],
                  until = -1;
                const positions = new Map<any, { column: number; columns: number }>();
                const flush = () => {
                  const ends: number[] = [];
                  for (const item of group) {
                    let column = ends.findIndex((end) => end <= item.start);
                    if (column < 0) column = ends.length;
                    ends[column] = Math.max(item.end, item.start + 24);
                    positions.set(item.e, { column, columns: 0 });
                  }
                  for (const item of group) positions.get(item.e)!.columns = ends.length;
                  group = [];
                };
                for (const item of segments) {
                  if (item.start >= until) {
                    flush();
                    until = -1;
                  }
                  group.push(item);
                  until = Math.max(until, item.end, item.start + 24);
                }
                flush();
                return (
                  <section
                    key={day}
                    className={
                      view === 'month'
                        ? `calendar-day ${day === isoDate(new Date()) ? 'today' : ''}`
                        : 'calendar-time-day'
                    }
                    aria-label={dateLabel(d)}
                  >
                    {view === 'month' ? (
                      <button className="calendar-date" onClick={() => create(day)}>
                        {d.getDate()}
                      </button>
                    ) : (
                      Array.from({ length: 24 }, (_, h) => (
                        <button
                          key={h}
                          className="calendar-slot"
                          aria-label={t('Créer le {day} à {hour}:00', { day, hour: h })}
                          style={{ top: h * 60 }}
                          onClick={() => create(day, h)}
                        />
                      ))
                    )}
                    {segments.map(({ e, start, end }) => {
                      const pos = positions.get(e)!;
                      return (
                        <button
                          key={e.id + e.occurrence.start}
                          className={`calendar-event ${e.cancelled ? 'cancelled' : ''} ${e.editable ? 'owned' : 'invited'}`}
                          style={
                            view === 'month'
                              ? undefined
                              : {
                                  position: 'absolute',
                                  top: start,
                                  height: Math.max(24, end - start),
                                  left: `${(pos.column * 100) / pos.columns}%`,
                                  width: `calc(${100 / pos.columns}% - 3px)`,
                                }
                          }
                          onClick={() =>
                            void act(async () => {
                              setForm(undefined);
                              setSelected(await api('/calendar/events/' + e.id));
                            })
                          }
                        >
                          <span>{time(e.occurrence.start)}</span>
                          <strong>{e.data.title}</strong>
                          <small>
                            {e.cancelled
                              ? t('Annulé')
                              : e.editable
                                ? t('Organisateur')
                                : t('Invitation')}
                          </small>
                        </button>
                      );
                    })}
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {(form || selected) && (
        <Modal
          title={form ? t('Événement') : t('Détails du rendez-vous')}
          onClose={() => {
            setForm(undefined);
            setSelected(undefined);
          }}
          wide
        >
          <div className="calendar-dialog-body">
            {error && <Notice type="error">{error}</Notice>}
            {form ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void act(async () => {
                    const body = {
                      ...form,
                      attendees: guestAddresses(form.attendees + ',' + (form.guestQuery || '')),
                    };
                    await send(
                      '/calendar/events' + (selected ? '/' + selected.id : ''),
                      body,
                      selected ? 'PUT' : 'POST',
                      { 'Idempotency-Key': requestKey },
                    );
                    setForm(undefined);
                    setSelected(undefined);
                    await load();
                  });
                }}
              >
                <h2>{selected ? t('Modifier la série') : t('Nouvel événement')}</h2>
                <label>
                  {t('Titre')}
                  <input
                    autoFocus
                    required
                    maxLength={200}
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                  />
                </label>
                <div className="calendar-form-row">
                  <label>
                    {t('Début')}
                    <input
                      type="datetime-local"
                      required
                      value={form.start}
                      onChange={(e) => setForm({ ...form, start: e.target.value })}
                    />
                  </label>
                  <label>
                    {t('Fin')}
                    <input
                      type="datetime-local"
                      required
                      value={form.end}
                      onChange={(e) => setForm({ ...form, end: e.target.value })}
                    />
                  </label>
                </div>
                <label>
                  {t('Fuseau de l’événement')}
                  <input
                    list="calendar-zones"
                    required
                    value={form.timezone}
                    onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  />
                </label>
                <label>
                  {t('Lieu')}
                  <input
                    maxLength={300}
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                  />
                </label>
                <label>
                  {t('Description')}
                  <textarea
                    maxLength={5000}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </label>
                <div className="calendar-form-row">
                  <label>
                    {t('Récurrence')}
                    <select
                      value={form.frequency}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          frequency: e.target.value,
                          count: e.target.value === 'once' ? 1 : 10,
                        })
                      }
                    >
                      <option value="once">{t('Une fois')}</option>
                      <option value="daily">{t('Chaque jour')}</option>
                      <option value="weekly">{t('Chaque semaine')}</option>
                      <option value="monthly">{t('Chaque mois')}</option>
                    </select>
                  </label>
                  {form.frequency !== 'once' && (
                    <label>
                      {t('Nombre de dates (max. 100)')}
                      <input
                        type="number"
                        min={1}
                        max={100}
                        required
                        value={form.count}
                        onChange={(e) => setForm({ ...form, count: Number(e.target.value) })}
                      />
                    </label>
                  )}
                  <label>
                    {t('Rappel')}
                    <select
                      value={form.reminderMinutes ?? 'none'}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          reminderMinutes:
                            e.target.value === 'none' ? null : Number(e.target.value),
                        })
                      }
                    >
                      <option value="none">{t('Aucun')}</option>
                      <option value="0">{t('À l’heure')}</option>
                      <option value="15">{t('15 minutes')}</option>
                      <option value="60">{t('1 heure')}</option>
                      <option value="1440">{t('1 jour')}</option>
                    </select>
                  </label>
                </div>
                <GuestPicker
                  value={form.attendees}
                  query={form.guestQuery || ''}
                  onChange={(value) =>
                    setForm((current: any) => ({ ...current, attendees: value }))
                  }
                  onQueryChange={(value) =>
                    setForm((current: any) => ({ ...current, guestQuery: value }))
                  }
                />
                <p className="muted">
                  {t(
                    'Enregistrer envoie une invitation calendrier aux invités. Modifier une série demande une nouvelle confirmation. Les rappels apparaissent lorsque l’agenda est ouvert.',
                  )}
                </p>
                <button className="primary" disabled={busy}>
                  {busy ? t('Enregistrement…') : t('Enregistrer et inviter')}
                </button>
              </form>
            ) : (
              selected && (
                <>
                  <h2>{selected.data.title}</h2>
                  <p>
                    {selected.data.start.replace('T', ' ')} → {selected.data.end.replace('T', ' ')}{' '}
                    · {selected.data.timezone}
                  </p>
                  <p>
                    {selected.data.frequency !== 'once'
                      ? t('{n} occurrences', { n: selected.occurrences.length })
                      : t('Événement unique')}{' '}
                    {selected.cancelled ? '· ' + t('Annulé') : ''}
                  </p>
                  <p>{selected.data.location}</p>
                  <p className="calendar-description">{selected.data.description}</p>
                  <p>Organisateur : {selected.organizer}</p>
                  <ul>
                    {selected.attendees.map((a: any) => (
                      <li key={a.email}>
                        {a.email} —{' '}
                        {
                          (
                            {
                              accepted: t('Accepté'),
                              declined: t('Refusé'),
                              tentative: t('Provisoire'),
                              'needs-action': t('En attente'),
                            } as any
                          )[a.response]
                        }
                      </li>
                    ))}
                  </ul>
                  <div className="calendar-actions">
                    <a className="secondary" href={`/v1/calendar/events/${selected.id}/ics`}>
                      {t('Télécharger le calendrier ICS')}
                    </a>
                    {!selected.cancelled &&
                      (selected.editable ? (
                        <>
                          <button
                            onClick={() =>
                              setForm({
                                ...selected.data,
                                revision: selected.revision,
                                attendees: selected.data.attendees.join(', '),
                              })
                            }
                          >
                            {t('Modifier la série')}
                          </button>
                          <button
                            disabled={busy}
                            onClick={() =>
                              void act(async () => {
                                await send(
                                  '/calendar/events/' + selected.id,
                                  { revision: selected.revision },
                                  'DELETE',
                                );
                                setSelected(undefined);
                                await load();
                              })
                            }
                          >
                            {t('Annuler l’événement')}
                          </button>
                        </>
                      ) : (
                        ['accepted', 'tentative', 'declined'].map((response, i) => (
                          <button
                            className={i === 0 ? 'primary' : 'secondary'}
                            disabled={busy}
                            key={response}
                            onClick={() =>
                              void act(async () => {
                                await send(`/calendar/events/${selected.id}/response`, {
                                  revision: selected.revision,
                                  response,
                                });
                                setSelected(await api('/calendar/events/' + selected.id));
                                await load();
                              })
                            }
                          >
                            {[t('Accepter'), t('Peut-être'), t('Refuser')][i]}
                          </button>
                        ))
                      ))}
                  </div>
                  {selected.editable &&
                    selected.deliveries?.map((j: any) => (
                      <p key={j.id}>
                        {t('Invitation v{n} :', { n: j.revision })}{' '}
                        {j.status === 'submitted'
                          ? t('prise en charge par la messagerie')
                          : j.status === 'superseded'
                            ? t('remplacée')
                            : t('en attente')}
                        {j.last_error ? ' — ' + j.last_error : ''}
                      </p>
                    ))}
                </>
              )
            )}
          </div>
        </Modal>
      )}
    </main>
  );
}
